<script setup>
import { ref, onMounted, watch } from 'vue';
import SignaturePad from 'signature_pad';

const props = defineProps({
  actionUrl: {
    type: String,
    required: true,
  },
  exportTypes: {
    type: String,
    required: true,
  },
  csrfToken: {
    type: String,
    required: true,
  },
  width: {
    type: Number,
    default: 400,
  },
  height: {
    type: Number,
    default: 200,
  },
  penColor: {
    type: String,
    default: 'black',
  },
  backgroundColor: {
    type: String,
    default: 'white',
  },
  lineWidth: {
    type: Number,
    default: 2.5,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
});

// Refs
const canvasRef = ref(null);
const FORMAT_PNG = 'png';
const FORMAT_JPEG = 'jpeg';
const FORMAT_SVG = 'svg';

let signaturePad = null;

// Initialize SignaturePad
onMounted(() => {
  const canvas = canvasRef.value;
  signaturePad = new SignaturePad(canvas, {
    penColor: props.penColor,
    backgroundColor: props.backgroundColor,
    minWidth: props.lineWidth,
    maxWidth: props.lineWidth,
  });

  if (props.readOnly) {
    signaturePad.off();
  }
});

// Watch for read-only prop changes
watch(() => props.readOnly, (newVal) => {
  if (newVal) {
    signaturePad.off();
  } else {
    signaturePad.on();
  }
});

// Clear signature canvas
const clearSignature = () => {
  signaturePad.clear();
};

// Undo last step
const undoLastStep = () => {
  const data = signaturePad.toData();
  if (data) {
    data.pop();
    signaturePad.fromData(data);
  }
};

// Convert data URL to Blob
const dataURLToBlob = (dataURL) => {
  const byteString = atob(dataURL.split(',')[1]);
  const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
};

// Save signature as PNG, JPEG, or SVG
const saveSignature = (format) => {
  let dataURL;
  if (format === 'png') {
    dataURL = signaturePad.toDataURL();
  } else if (format === 'jpeg') {
    dataURL = signaturePad.toDataURL('image/jpeg');
  } else if (format === 'svg') {
    dataURL = signaturePad.toSVG();
  }

  const blob = dataURLToBlob(dataURL);
  const formData = new FormData();
  formData.append('_token', props.csrfToken);
  formData.append('signature', blob, `signature.${format}`);

  axios.post(props.actionUrl, formData)
      .then(response => {
        console.log('Signature saved successfully');
      })
      .catch(error => {
        console.error('Error saving signature', error);
      });
};

function isValueInArray(value, array) {
  return array.includes(value);
}

// Parse exportTypes prop
const exportTypesArray = JSON.parse(props.exportTypes);

</script>

<template>
  <div>
    <canvas class="tw-border tw-border-black tw-w-full" ref="canvasRef" :width="width" :height="height"></canvas>
    <div class="tw-mt-2 tw-justify-between tw-flex">
      <div>
        <a class="cu-btn-default" @click="clearSignature"> <i class="fa fa-times"></i> Löschen </a>
        <a class="cu-btn-default" @click="undoLastStep"> <i class="fa fa-undo"></i> Zurück </a>
      </div>
      <div>
        <a class="cu-btn-default" @click="saveSignature(FORMAT_PNG)" v-if="isValueInArray(FORMAT_PNG, exportTypesArray)">Als PNG speichern</a>
        <a class="cu-btn-default" @click="saveSignature(FORMAT_JPEG)" v-if="isValueInArray(FORMAT_JPEG, exportTypesArray)">Als JPEG speichern</a>
        <a class="cu-btn-default" @click="saveSignature(FORMAT_SVG)" v-if="isValueInArray(FORMAT_SVG, exportTypesArray)">Als SVG speichern</a>
      </div>
    </div>
  </div>
</template>
